/**
 * Development-only failure switches.
 *
 * Every mock method consults these so QA can exercise the states that are
 * otherwise impossible offline: a dead network, an empty menu, a rejected
 * payment, an expired share link, a dropped voice socket.
 *
 * Set by automated QA through the storage fixture; no guest-facing dev panel.
 */
import { readJson, storageKeys, writeJson } from '../lib/storage/local';

export type ScenarioKey =
  | 'networkFailure'
  | 'emptyMenu'
  | 'itemUnavailable'
  | 'paymentFailure'
  | 'expiredShare'
  | 'voiceDisconnected'
  | 'assistantDown'
  | 'slowNetwork';

export type ScenarioState = Record<ScenarioKey, boolean>;

export const SCENARIO_LABELS: Record<ScenarioKey, string> = {
  networkFailure: 'خطای شبکه',
  emptyMenu: 'منوی خالی',
  itemUnavailable: 'اتمام موجودی آیتم',
  paymentFailure: 'پرداخت ناموفق',
  expiredShare: 'لینک سهم منقضی',
  voiceDisconnected: 'قطع اتصال صوتی',
  assistantDown: 'دستیار در دسترس نیست',
  slowNetwork: 'شبکهٔ کند',
};

const DEFAULT_STATE: ScenarioState = {
  networkFailure: false,
  emptyMenu: false,
  itemUnavailable: false,
  paymentFailure: false,
  expiredShare: false,
  voiceDisconnected: false,
  assistantDown: false,
  slowNetwork: false,
};

let state: ScenarioState = { ...DEFAULT_STATE, ...readJson(storageKeys.devScenario, {}) };

type Listener = (next: ScenarioState) => void;
const listeners = new Set<Listener>();

export const scenarios = {
  get(): ScenarioState {
    return state;
  },
  isOn(key: ScenarioKey): boolean {
    return state[key];
  },
  set(key: ScenarioKey, value: boolean): void {
    state = { ...state, [key]: value };
    writeJson(storageKeys.devScenario, state);
    listeners.forEach((listener) => listener(state));
  },
  reset(): void {
    state = { ...DEFAULT_STATE };
    writeJson(storageKeys.devScenario, state);
    listeners.forEach((listener) => listener(state));
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
